/**
 * /developers — public API & integration guide for SocialCapital.cc.
 *
 * Renders two clearly-separated sections:
 *   1. Sister-site integration — how internal SocialPulse brands
 *      (foodpulse.net, indianfamilytree.com, etc.) push points via the
 *      shared monolith + site-key auth.
 *   2. Third-party integration — how outside orgs can read passports
 *      today (public REST) and what write-side support is coming.
 *
 * Pure static content; two tabs; anchored subsections so employers /
 * partner engineers can send deep-links to teammates.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Terminal, Building2, ExternalLink, Copy, Check, Users2, ArrowRight,
  KeyRound, Sparkles, ShieldCheck,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { toast } from "sonner";

const BASE_URL_PROD = process.env.REACT_APP_SOCIAL_CAPITAL_API_BASE
  || process.env.REACT_APP_BACKEND_URL
  || "https://cross-brand-deploy.emergent.host";

const SISTER_ENDPOINTS = [
  ["GET",  "/social-capital/portfolio/{who}",             "Full passport view"],
  ["GET",  "/social-capital/timeline/{who}?days=30",      "Daily activity sparkline"],
  ["GET",  "/social-capital/contributions/{who}?category=", "Feeds endorse-deeplink picker"],
  ["GET",  "/social-capital/members?category=",           "Public member directory"],
  ["GET",  "/social-capital/impact-summary",              "Network-wide impact totals"],
  ["GET",  "/social-capital/categories",                  "8 canonical buckets"],
  ["GET",  "/endorsements/received/{who}",                "Public endorsement wall"],
  ["GET",  "/endorsements/given/{who}",                   "Endorsements a member has given"],
  ["GET",  "/badges/{who}",                               "Earned badges"],
  ["GET",  "/badges/catalog",                             "Every defined badge"],
  ["POST", "/site/ping",                                  "Auth probe — validates X-Site-Key"],
  ["POST", "/social-capital/submissions",                 "Sibling submits an action for review"],
  ["POST", "/endorsements",                               "Signed-in user endorses another"],
];

const THIRD_PARTY_ENDPOINTS = [
  ["GET",  "/social-capital/portfolio/{handle}",   "Public",     "Full passport for embeds / verification"],
  ["GET",  "/social-capital/timeline/{handle}",    "Public",     "Zero-filled daily point buckets"],
  ["GET",  "/social-capital/impact-summary",       "Public",     "Widget-friendly network totals"],
  ["GET",  "/social-capital/members",              "Public",     "Directory (respects privacy flags)"],
  ["GET",  "/endorsements/received/{handle}",      "Public",     "Vouches from other members"],
  ["GET",  "/badges/{handle}",                     "Public",     "Earned badges"],
  ["POST", "/social-capital/submissions",          "User JWT",   "User submits an action to their own passport"],
  ["POST", "/endorsements",                        "User JWT",   "User endorses another"],
  ["POST", "/partners/contributions",              "Partner key",  "🚧 Server-to-server writes — cohort onboarding"],
  ["POST", "/partners/webhooks",                   "Partner key",  "🚧 Subscribe to passport events"],
];

const TABS = [
  { key: "sister",  label: "Sister Sites",      Icon: Building2 },
  { key: "third",   label: "Third-Party",       Icon: ExternalLink },
];

function CodeBlock({ children, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(children);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <pre
      className="relative rounded-xl border overflow-x-auto p-4 text-xs sm:text-sm leading-relaxed font-mono"
      style={{ background: "#0F172A", color: "#E2E8F0", borderColor: "rgba(255,255,255,0.08)" }}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 rounded-md px-2 py-1 text-[10px] inline-flex items-center gap-1 opacity-70 hover:opacity-100"
        style={{ background: "rgba(255,255,255,0.10)", color: "#F8FAFC" }}
        aria-label="Copy code"
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
      <code>{children}</code>
    </pre>
  );
}

function EndpointTable({ rows, wide }) {
  return (
    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
      <table className="min-w-full text-sm">
        <thead style={{ background: "#F9FAFB" }}>
          <tr>
            <th className="px-3 py-2 text-left w-16">Method</th>
            <th className="px-3 py-2 text-left">Path</th>
            {wide && <th className="px-3 py-2 text-left w-32">Auth</th>}
            <th className="px-3 py-2 text-left">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.join("|")} className="border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
              <td className="px-3 py-2 font-mono text-xs">
                <span className="inline-block rounded px-1.5 py-0.5 font-semibold"
                      style={{
                        background: r[0] === "GET" ? "#DCFCE7" : "#DBEAFE",
                        color:      r[0] === "GET" ? "#065F46" : "#1E40AF",
                      }}>
                  {r[0]}
                </span>
              </td>
              <td className="px-3 py-2 font-mono text-xs break-all">{r[1]}</td>
              {wide && <td className="px-3 py-2 text-xs opacity-80">{r[2]}</td>}
              <td className="px-3 py-2 text-xs opacity-80">{wide ? r[3] : r[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SocialCapitalDevDocsPage() {
  const [tab, setTab] = useState("sister");

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="sc-dev-docs">
      <SeoHead
        section="social-capital"
        title="Developer API · SocialCapital.cc"
        description="Integration docs for SocialPulse sister sites and third-party developers — read passports, push verified contributions, embed impact widgets."
        url="/developers"
      />

      {/* Hero */}
      <header className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 inline-flex items-center gap-1.5"
             style={{ color: "#059669" }}>
          <Terminal className="w-3.5 h-3.5" /> Developers · API guide
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold" style={{ color: "#0f172a" }}>
          Build with Social Capital.
        </h1>
        <p className="mt-4 text-base sm:text-lg opacity-80 max-w-2xl">
          Two integration paths — one for sister SocialPulse brands who
          share the network's shared Atlas cluster, one for outside
          organisations who want to read passports or push verified
          contributions on a user's behalf.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-mono"
             style={{ background: "#ECFDF5", color: "#065F46" }}
             data-testid="sc-dev-base-url">
          Base URL · {BASE_URL_PROD}/api
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap items-center gap-2 mb-6" role="tablist" data-testid="sc-dev-tabs">
        {TABS.map(({ key, label, Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={active}
              data-testid={`sc-dev-tab-${key}`}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
              style={{
                background: active ? "#059669" : "#F1F5F9",
                color:      active ? "#F0FDF4" : "#0F172A",
              }}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          );
        })}
      </nav>

      {tab === "sister" ? <SisterSection /> : <ThirdPartySection />}

      {/* Footer CTA */}
      <aside
        className="mt-12 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4"
        style={{ borderColor: "rgba(5,150,105,0.25)", background: "#ECFDF5" }}
      >
        <div className="max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5"
               style={{ color: "#059669" }}>
            <Sparkles className="w-3 h-3" /> Need a partner key?
          </div>
          <div className="font-display text-lg sm:text-xl mt-1">
            We onboard third-party integrators in cohorts — tell us what
            you're building and we'll add you to the next wave.
          </div>
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white"
          style={{ background: "#059669" }}
          data-testid="sc-dev-contact-cta"
        >
          Contact the API team <ArrowRight className="w-4 h-4" />
        </Link>
      </aside>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Sister-site section                                            */
/* -------------------------------------------------------------- */
function SisterSection() {
  return (
    <div className="space-y-8" data-testid="sc-dev-sister">
      <Section title="1 · Shared architecture" Icon={Users2}>
        <p className="opacity-85 leading-relaxed">
          Every sibling site (FoodPulse, IndianFamilyTree, KeralaTravels,
          NaturePulse, FarmersPulse, AlumniPulse, SocialPulse hub, MFT
          Care, and future additions) is an independent Emergent
          deployment that reads and writes the <b>same MongoDB Atlas
          cluster</b>. That means an action logged on foodpulse.net
          immediately flows into the same user's passport on
          socialcapital.cc — no cross-domain syncing required.
        </p>
        <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
          {["socialcapital", "socialpulse", "foodpulse", "indianfamilytree",
            "kerala", "keralatravels", "naturepulse", "farmerspulse",
            "alumnipulse", "mftcare"].map((s) => (
              <li key={s} className="rounded-lg px-3 py-2 font-mono text-xs"
                  style={{ background: "#F9FAFB", color: "#0F172A" }}>
                site_source = <b>{s}</b>
              </li>
          ))}
        </ul>
      </Section>

      <Section title="2 · Site-key authentication" Icon={KeyRound}>
        <p className="opacity-85 leading-relaxed mb-3">
          Write-adjacent calls include an admin-issued site key.
          Public GETs need no auth at all.
        </p>
        <CodeBlock testid="sc-dev-key-mint">{`# Mint a key (admin JWT required — one-time only)
curl -X POST "$BASE/api/admin/site-keys" \\
  -H "Authorization: Bearer $ADMIN_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{"site_source":"indianfamilytree","label":"IFT prod ingest","scope":"write"}'

# Response includes the raw key ONCE:
{ "ok": true, "id": "…", "key": "sp_live_ind_XXXXXXXX",
  "warning": "Copy this key now — it will not be shown again." }`}</CodeBlock>
        <p className="opacity-85 leading-relaxed mt-4 mb-3">
          Then include on every request:
        </p>
        <CodeBlock testid="sc-dev-key-headers">{`X-Site-Source: indianfamilytree
X-Site-Key:    sp_live_ind_XXXXXXXX

# Or use bearer form:
Authorization: Bearer sp_live_ind_XXXXXXXX`}</CodeBlock>
      </Section>

      <Section title="3 · Writing points" Icon={ShieldCheck}>
        <p className="opacity-85 leading-relaxed mb-3">
          Since sisters share the same Python codebase, prefer a direct
          call to <code className="text-xs px-1 py-0.5 rounded" style={{background:"#F1F5F9"}}>deps.add_points()</code> from
          the route where the action verifies. It's atomic and
          audit-safe.
        </p>
        <CodeBlock testid="sc-dev-add-points">{`from deps import add_points

await add_points(
    user_id=user["id"],
    points=25,
    reason=f"Rescued {meals} meals — Blanchet House, {city}",
    category="save_food",       # one of the 8 canonical buckets
    site_source="foodpulse",    # ← your brand
    verified=True,              # only if durably cross-checked
)`}</CodeBlock>
        <p className="opacity-85 leading-relaxed mt-4 mb-3">
          For user-facing "I did this" flows where you want a moderator
          to review before points land, use the pending-queue endpoint:
        </p>
        <CodeBlock testid="sc-dev-submit">{`curl -X POST "$BASE/api/social-capital/submissions" \\
  -H "X-Site-Source: indianfamilytree" \\
  -H "X-Site-Key:    sp_live_ind_XXXXXXXX" \\
  -H "Content-Type: application/json" \\
  -d '{
    "category":     "help_people",
    "title":        "Drove elder neighbour to hospital, 42 km round trip",
    "description":  "…",
    "hours":        3.5,
    "people_impacted": 1,
    "evidence_url": "https://…"
  }'`}</CodeBlock>
      </Section>

      <Section title="4 · Endpoint reference" Icon={Terminal}>
        <EndpointTable rows={SISTER_ENDPOINTS} />
        <p className="text-xs opacity-70 mt-3">
          Full spec, canonical categories, rate-limit policy and site_source
          slugs live in <code>/app/memory/API_SISTER_SITES.md</code>.
        </p>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Third-party section                                            */
/* -------------------------------------------------------------- */
function ThirdPartySection() {
  return (
    <div className="space-y-8" data-testid="sc-dev-third">
      <Section title="1 · What's available today" Icon={Terminal}>
        <p className="opacity-85 leading-relaxed">
          Everything you need to <b>read</b> a passport is live and
          requires no onboarding. <b>Write</b> paths that let a user
          push actions to their own passport also work with the
          standard user JWT. Server-to-server writes for partner
          platforms are onboarded in cohorts — <Link to="/contact" className="font-semibold underline" style={{color:"#059669"}}>request access</Link>.
        </p>
      </Section>

      <Section title="2 · Verify a passport" Icon={ShieldCheck}>
        <p className="opacity-85 leading-relaxed mb-3">
          Employers, admissions committees and volunteer platforms use
          the portfolio endpoint to verify a candidate's public passport.
          Every field returned is data the user chose to publish.
        </p>
        <CodeBlock testid="sc-dev-3p-portfolio">{`curl "$BASE/api/social-capital/portfolio/meera-nair" | jq '{
  sc:     .user.social_capital,
  tier:   .user.verification_tier,
  level:  .user.level,
  joined: .user.created_at
}'`}</CodeBlock>
      </Section>

      <Section title="3 · User-authorised writes" Icon={KeyRound}>
        <p className="opacity-85 leading-relaxed mb-3">
          Once your user has logged in through the SocialPulse OAuth
          flow, forward their bearer token to record a contribution:
        </p>
        <CodeBlock testid="sc-dev-3p-submit">{`curl -X POST "$BASE/api/social-capital/submissions" \\
  -H "Authorization: Bearer $USER_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{
    "category":       "volunteer_serve",
    "title":          "Weekly kitchen shift",
    "description":    "Cooked & served dinner at Blanchet House",
    "hours":          3,
    "people_impacted": 45,
    "evidence_url":   "https://blanchethouse.org/…"
  }'`}</CodeBlock>
        <p className="text-xs opacity-70 mt-3">
          Every submission lands in <code>action_reports</code> as
          <code> status: "pending"</code> and needs moderator approval
          before it counts toward the user's passport.
        </p>
      </Section>

      <Section title="4 · Coming soon — partner keys" Icon={Sparkles}>
        <p className="opacity-85 leading-relaxed mb-3">
          Volunteer platforms, HR systems and civic dashboards that
          need to push contributions <b>on behalf of a user</b> without
          the user being present in-session will use partner-scoped
          API keys. Draft interface:
        </p>
        <CodeBlock testid="sc-dev-3p-partner">{`curl -X POST "$BASE/api/partners/contributions" \\
  -H "X-Partner-Id:  hr-platform-alpha" \\
  -H "X-Partner-Key: pk_live_XXXXXXXX" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_lookup": { "email": "meera@example.com" },
    "category":    "volunteer_serve",
    "hours":       3.5,
    "reason":      "Volunteer shift — Blanchet House kitchen",
    "occurred_on": "2026-02-24",
    "evidence":    { "signed_url": "https://…" }
  }'`}</CodeBlock>
        <p className="opacity-85 leading-relaxed mt-3 text-sm">
          Every partner push is moderator-gated at launch. Trusted
          partners can be upgraded to per-category auto-approve.
        </p>
      </Section>

      <Section title="5 · Endpoint reference" Icon={Terminal}>
        <EndpointTable rows={THIRD_PARTY_ENDPOINTS} wide />
        <p className="text-xs opacity-70 mt-3">
          Full spec, GDPR notes and attribution guidelines live in
          <code> /app/memory/API_THIRD_PARTY.md</code>.
        </p>
      </Section>

      <Section title="6 · Rate limits" Icon={ShieldCheck}>
        <ul className="list-disc pl-6 text-sm opacity-85 space-y-1">
          <li>Public reads · 60 req/min anonymous, 600 req/min with a partner key.</li>
          <li>User-JWT writes · 30 req/min per user.</li>
          <li>Translation · 10 req/min anonymous, 60 req/min keyed.</li>
          <li>Exceeding any limit returns <code>HTTP 429</code> with a <code>Retry-After</code> header.</li>
        </ul>
      </Section>

      <SignedWebhooksSection base={BASE_URL_PROD} />

      <Section title="7 · Embed widget — drop-in <script>" Icon={Sparkles}>
        <p className="opacity-85 leading-relaxed mb-3">
          Ship a live Social Capital pill on your careers page, alumni
          directory or bio page. Zero deps, no cookies, ~2 KB gzipped.
          The pill fetches the public passport and links back to
          socialcapital.cc.
        </p>
        <CodeBlock testid="sc-dev-embed-snippet">{`<!-- 1. Place the pill anywhere on your page -->
<div data-sc-pill data-handle="meera-nair"></div>

<!-- 2. Load the widget once, near the end of <body> -->
<script async src="${BASE_URL_PROD}/embed/sc-pill.js"></script>`}</CodeBlock>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border p-4"
               style={{ borderColor: "rgba(0,0,0,0.08)", background: "#F9FAFB" }}>
            <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">Optional attributes</div>
            <ul className="text-xs space-y-1 font-mono">
              <li><b>data-handle</b> — required. Public handle or user id.</li>
              <li><b>data-theme</b> — "light" (default) | "dark"</li>
              <li><b>data-size</b> — "s" | "m" (default) | "l"</li>
            </ul>
          </div>
          <div className="rounded-xl border p-4 flex items-center justify-center"
               style={{ borderColor: "rgba(0,0,0,0.08)", background: "#FFFFFF" }}>
            <div className="text-xs opacity-60 mb-2">Preview</div>
            <a href="/passport/me" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5"
               style={{ background: "#ECFDF5", color: "#065F46", borderColor: "rgba(0,0,0,0.08)" }}>
              <span className="w-5 h-5 rounded-full text-white text-[10px] font-bold inline-flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg,#059669,#10B981)" }}>SC</span>
              <span className="text-xs font-semibold">Meera Nair</span>
              <span className="text-xs font-bold tabular-nums">3.2K</span>
              <span className="text-[10px] uppercase tracking-wider opacity-70">SC</span>
            </a>
          </div>
        </div>
      </Section>

      <PartnerSignupForm />

      <Section title="9 · OpenAPI 3.1 spec" Icon={Terminal}>
        <p className="opacity-85 leading-relaxed mb-3">
          Import the machine-readable spec directly into Postman, Insomnia,
          or any OpenAPI-compatible tool.
        </p>
        <CodeBlock testid="sc-dev-openapi">{`# Live spec
${BASE_URL_PROD}/api/partners/openapi.json

# Import into Postman
File → Import → Link → paste the URL above`}</CodeBlock>
        <div className="mt-4">
          <a href={`${BASE_URL_PROD}/api/partners/openapi.json`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border"
             style={{ borderColor: "rgba(0,0,0,0.15)" }}
             data-testid="sc-dev-openapi-link">
            View spec <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Partner signup form                                            */
/* -------------------------------------------------------------- */
function PartnerSignupForm() {
  const [form, setForm] = useState({
    org_name: "", website: "", contact_name: "", contact_email: "",
    use_case: "", expected_volume_per_month: "", categories: [],
  });
  const [saving, setSaving] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const CATS = ["help_people","save_food","protect_nature","support_farmers",
                "share_knowledge","reuse_give","volunteer_serve","build_community"];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (c) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name || !form.website || !form.contact_name
        || !form.contact_email || form.use_case.trim().length < 20) {
      toast.error("Please fill in every field (use-case at least 20 chars).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        expected_volume_per_month: form.expected_volume_per_month
          ? Number(form.expected_volume_per_month) : null,
      };
      const { data } = await http.post("/partners/apply", payload);
      setApplicationId(data?.application_id || "");
      toast.success("Application submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border p-6 sm:p-8 bg-white"
             style={{ borderColor: "rgba(0,0,0,0.08)" }}
             data-testid="sc-dev-signup">
      <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
        <KeyRound className="w-5 h-5" style={{ color: "#059669" }} />
        8 · Request a partner key
      </h2>
      {applicationId ? (
        <div className="rounded-xl p-5 text-center"
             style={{ background: "#ECFDF5", color: "#065F46" }}
             data-testid="sc-dev-signup-success">
          <Check className="w-8 h-8 mx-auto mb-2" />
          <div className="font-display text-lg">Application received.</div>
          <p className="text-sm mt-1 opacity-80">
            Reference <b>{applicationId}</b>. We typically respond within a few business days.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 max-w-2xl">
          <p className="opacity-85 leading-relaxed text-sm">
            Self-serve application — filled by a human on your team.
            A moderator reviews within a few business days and emails
            the raw key to your contact address.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <SignupField label="Organisation" required
                         value={form.org_name} onChange={(v) => set("org_name", v)}
                         testid="sc-dev-signup-org" />
            <SignupField label="Website (https://)" required
                         value={form.website} onChange={(v) => set("website", v)}
                         placeholder="https://…" testid="sc-dev-signup-website" />
            <SignupField label="Contact name" required
                         value={form.contact_name} onChange={(v) => set("contact_name", v)}
                         testid="sc-dev-signup-name" />
            <SignupField label="Contact email" required type="email"
                         value={form.contact_email} onChange={(v) => set("contact_email", v)}
                         testid="sc-dev-signup-email" />
          </div>
          <SignupField label="How will you use the API?" required textarea
                       hint="At least 20 characters. Concrete beats aspirational."
                       value={form.use_case} onChange={(v) => set("use_case", v)}
                       testid="sc-dev-signup-use-case" />
          <div className="grid sm:grid-cols-2 gap-3">
            <SignupField label="Expected volume / month (optional)" type="number"
                         value={form.expected_volume_per_month}
                         onChange={(v) => set("expected_volume_per_month", v)}
                         testid="sc-dev-signup-volume" />
            <div>
              <div className="text-xs font-mono uppercase tracking-widest mb-1 opacity-70">
                Interested categories (optional)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CATS.map((c) => {
                  const on = form.categories.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleCat(c)}
                            className="rounded-full px-2 py-0.5 text-[11px] font-mono transition-colors"
                            style={{
                              background: on ? "#059669" : "#F1F5F9",
                              color:      on ? "#F0FDF4" : "#0F172A",
                            }}
                            data-testid={`sc-dev-signup-cat-${c}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <button type="submit" disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#059669" }}
                  data-testid="sc-dev-signup-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {saving ? "Submitting…" : "Submit application"}
          </button>
        </form>
      )}
    </section>
  );
}

function SignupField({ label, hint, value, onChange, required, type, textarea, placeholder, testid }) {
  return (
    <label className="block text-sm">
      <div className="font-semibold mb-1">
        {label} {required && <span style={{ color: "#DC2626" }}>*</span>}
      </div>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
                  rows={4} placeholder={placeholder}
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  data-testid={testid} />
      ) : (
        <input type={type || "text"} value={value}
               onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder}
               className="w-full rounded-lg border px-3 py-2 text-sm"
               style={{ borderColor: "rgba(0,0,0,0.15)" }}
               data-testid={testid} />
      )}
      {hint && <div className="text-xs opacity-60 mt-1">{hint}</div>}
    </label>
  );
}

/* -------------------------------------------------------------- */
/*  Signed webhooks section                                        */
/* -------------------------------------------------------------- */
function SignedWebhooksSection({ base }) {
  const nodeSnippet = `import crypto from "crypto";
import express from "express";

const SIGNING_SECRET = process.env.SC_WEBHOOK_SECRET; // whsec_...

const app = express();
app.use(express.json({
  verify: (req, _res, buf) => { req.rawBody = buf.toString("utf8"); }
}));

app.post("/webhook", (req, res) => {
  const ts  = req.header("X-Sc-Timestamp");
  const sig = (req.header("X-Sc-Signature") || "").replace(/^sha256=/, "");
  if (!ts || !sig) return res.status(400).send("missing headers");

  // Reject replays older than 5 minutes.
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300)
    return res.status(400).send("stale");

  const expected = crypto
    .createHmac("sha256", SIGNING_SECRET)
    .update(ts + "." + req.rawBody)
    .digest("hex");

  if (!crypto.timingSafeEqual(Buffer.from(sig, "hex"),
                              Buffer.from(expected, "hex")))
    return res.status(400).send("bad signature");

  const { event, data } = req.body;
  console.log("verified", event, data);
  res.sendStatus(200);
});

app.listen(3000);`;

  const pythonSnippet = `import hmac, hashlib, os, time
from fastapi import FastAPI, Request, HTTPException

SIGNING_SECRET = os.environ["SC_WEBHOOK_SECRET"]  # whsec_...
app = FastAPI()

@app.post("/webhook")
async def sc_webhook(req: Request):
    ts  = req.headers.get("X-Sc-Timestamp")
    sig = (req.headers.get("X-Sc-Signature") or "").replace("sha256=", "")
    if not ts or not sig:
        raise HTTPException(400, "missing headers")

    # Reject replays older than 5 minutes.
    if abs(time.time() - int(ts)) > 300:
        raise HTTPException(400, "stale")

    body = await req.body()
    expected = hmac.new(
        SIGNING_SECRET.encode(),
        (ts + ".").encode() + body,
        hashlib.sha256,
    ).hexdigest()

    if not hmac.compare_digest(sig, expected):
        raise HTTPException(400, "bad signature")

    payload = __import__("json").loads(body)
    print("verified", payload["event"], payload["data"])
    return {"ok": True}`;

  return (
    <section className="rounded-2xl border p-6 sm:p-8 bg-white"
             style={{ borderColor: "rgba(0,0,0,0.08)" }}
             data-testid="sc-dev-webhooks">
      <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" style={{ color: "#059669" }} />
        10 · Signed webhooks
      </h2>
      <p className="opacity-85 leading-relaxed mb-3">
        Subscribe to real-time events instead of polling. Every delivery
        is signed with your <code>whsec_…</code> secret using HMAC-SHA256
        — verify it in three lines of code below. Reject replays older
        than 5 minutes.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {[
          ["contribution.approved", "Auto-approved partner push landed on a user's passport."],
          ["endorsement.received",  "A user was thanked by another member."],
        ].map(([evt, desc]) => (
          <div key={evt} className="rounded-xl border p-4"
               style={{ borderColor: "rgba(0,0,0,0.08)", background: "#F9FAFB" }}>
            <div className="font-mono text-xs font-bold" style={{ color: "#059669" }}>
              {evt}
            </div>
            <div className="text-xs opacity-75 mt-1">{desc}</div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Create a subscription
        </div>
        <CodeBlock testid="sc-dev-webhooks-create">{`curl -X POST "${base}/api/partners/webhooks" \\
  -H "Authorization: Bearer pk_live_XXXXXXXX" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url":    "https://your-app.example.com/webhook",
    "events": ["contribution.approved", "endorsement.received"]
  }'

# Response — save the signing_secret now, it will not be shown again:
{
  "ok": true,
  "webhook_id": "…",
  "signing_secret": "whsec_XXXXXXXXXXXXXXXXXXXXXX"
}`}</CodeBlock>
      </div>

      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Delivery headers
        </div>
        <CodeBlock testid="sc-dev-webhooks-headers">{`X-Sc-Event:      contribution.approved
X-Sc-Timestamp:  1735689600
X-Sc-Signature:  sha256=<hex-hmac-of "timestamp.body">
X-Sc-Webhook-Id: <your webhook id>
User-Agent:      SocialCapital-Webhooks/1.0`}</CodeBlock>
      </div>

      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Verify — Node.js
        </div>
        <CodeBlock testid="sc-dev-webhooks-node">{nodeSnippet}</CodeBlock>
      </div>

      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Verify — Python (FastAPI)
        </div>
        <CodeBlock testid="sc-dev-webhooks-python">{pythonSnippet}</CodeBlock>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Test your integration
        </div>
        <CodeBlock testid="sc-dev-webhooks-test">{`# Fire a webhook.test payload at your URL so you can iterate on
# signature verification without waiting for a real event.
curl -X POST "${base}/api/partners/webhooks/<webhook-id>/test" \\
  -H "Authorization: Bearer pk_live_XXXXXXXX"`}</CodeBlock>
      </div>
    </section>
  );
}

function Section({ title, Icon, children }) {
  return (
    <section className="rounded-2xl border p-6 sm:p-8 bg-white"
             style={{ borderColor: "rgba(0,0,0,0.08)" }}>
      <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color: "#059669" }} />
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
